import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Check, X } from "lucide-react";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Load Google Maps JS API once
let gmapsPromise = null;
const loadGoogleMaps = () => {
  if (gmapsPromise) return gmapsPromise;
  if (window.google?.maps?.Map) return Promise.resolve();
  gmapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return gmapsPromise;
};

const PincodeMapPicker = ({ open, onClose, pincode, shipments, initialSelected, onConfirm }) => {
  const [selected, setSelected] = useState(new Set());
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const selectedRef = useRef(new Set());

  // Keep ref in sync with state (for marker click handlers)
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  // Initialize selection from props
  useEffect(() => {
    if (open) {
      const init = new Set(initialSelected || shipments.map(s => s.id));
      setSelected(init);
      selectedRef.current = init;
    }
  }, [open, initialSelected, shipments]);

  // Load Google Maps and create map
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const init = async () => {
      await loadGoogleMaps();
      if (cancelled || !mapRef.current) return;

      // Wait a tick for dialog to have dimensions
      await new Promise(r => setTimeout(r, 300));
      if (cancelled || !mapRef.current) return;

      const withCoords = shipments.filter(s => s.coords?.lat && s.coords?.lng);
      if (withCoords.length === 0) return;

      const center = {
        lat: withCoords.reduce((s, c) => s + c.coords.lat, 0) / withCoords.length,
        lng: withCoords.reduce((s, c) => s + c.coords.lng, 0) / withCoords.length,
      };

      const map = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      mapInstanceRef.current = map;

      // Fit bounds
      if (withCoords.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();
        withCoords.forEach(s => bounds.extend({ lat: s.coords.lat, lng: s.coords.lng }));
        map.fitBounds(bounds, 50);
      }

      setMapReady(true);
    };

    init();

    return () => {
      cancelled = true;
      // Clean up markers
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
      mapInstanceRef.current = null;
      setMapReady(false);
    };
  }, [open, shipments]);

  // Render markers (re-runs when selection or mapReady changes)
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    const map = mapInstanceRef.current;
    const withCoords = shipments.filter(s => s.coords?.lat && s.coords?.lng);

    // Clear old markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    withCoords.forEach(s => {
      const isSel = selected.has(s.id);

      const marker = new window.google.maps.Marker({
        position: { lat: s.coords.lat, lng: s.coords.lng },
        map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: isSel ? "#16a34a" : "#94a3b8",
          fillOpacity: 1,
          strokeColor: isSel ? "#15803d" : "#64748b",
          strokeWeight: 2,
        },
        title: s.shipmentId,
        zIndex: isSel ? 2 : 1,
      });

      const info = new window.google.maps.InfoWindow({
        content: `<div style="font-size:12px;max-width:220px;padding:2px">
          <strong>${s.shipmentId}</strong><br/>
          <span style="color:#666">${s.type} · ${s.paymentType}</span>
          ${s.address ? `<br/><span style="color:#888;font-size:11px">${s.address}</span>` : ""}
          <br/><span style="color:${isSel ? '#16a34a' : '#94a3b8'};font-weight:600;font-size:11px">${isSel ? "SELECTED" : "UNSELECTED"}</span>
        </div>`,
      });

      marker.addListener("mouseover", () => info.open(map, marker));
      marker.addListener("mouseout", () => info.close());

      marker.addListener("click", () => {
        setSelected(prev => {
          const next = new Set(prev);
          if (next.has(s.id)) next.delete(s.id);
          else next.add(s.id);
          return next;
        });
      });

      markersRef.current.push(marker);
    });
  }, [mapReady, selected, shipments]);

  const handleConfirm = () => {
    onConfirm(Array.from(selected));
  };

  const noCoords = shipments.filter(s => !s.coords?.lat || !s.coords?.lng);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <MapPin className="w-5 h-5 text-emerald-600" />
            Pick Shipments — Pincode {pincode}
            <Badge variant="outline" className="ml-2 text-xs">
              {selected.size}/{shipments.length} selected
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Legend */}
        <div className="px-4 pb-2 flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-green-600 inline-block" /> Selected
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-slate-400 inline-block" /> Unselected
          </span>
          <span className="ml-auto">Click a marker to toggle selection</span>
        </div>

        {/* Map Container */}
        <div
          ref={mapRef}
          className="w-full border-y"
          style={{ height: "55vh", minHeight: 350 }}
          data-testid="pincode-map-picker"
        />

        {/* No-coords warning */}
        {noCoords.length > 0 && (
          <div className="px-4 py-1.5 text-xs text-amber-600 bg-amber-50">
            {noCoords.length} shipment(s) without coordinates (auto-included in selection)
          </div>
        )}

        {/* Quick actions */}
        <div className="px-4 py-2 flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-xs h-7"
            onClick={() => setSelected(new Set(shipments.map(s => s.id)))}>
            Select All ({shipments.length})
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-7"
            onClick={() => setSelected(new Set())}>
            Clear All
          </Button>
        </div>

        <DialogFooter className="px-4 pb-4 gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selected.size === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="map-picker-confirm"
          >
            <Check className="w-4 h-4 mr-1" /> Confirm {selected.size} shipment{selected.size !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PincodeMapPicker;
